import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Loading } from 'module/ui/components/atoms/loading';
import { HelperText01 } from 'module/ui/components/styles';
import { Body01 } from 'module/ui/ozone/atoms/typography/body';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { Tooltip } from 'antd';
import { Meeting } from 'api/meeting/types';
import moment from 'moment';
import { useGetAllMeetingsQueryWithScrollPagination } from 'queries/meeting';
import { useCallback, useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';
import styled from 'styled-components';

const RowWrapper = styled.div`
  /* Wrapper gets react-window style; inner MenuItem can use margin and border-radius */
  height: 100%;
  box-sizing: border-box;
`;

const MenuItem = styled.div<{ isSelected: boolean }>`
  min-width: 200px;
  width: calc(100% - 2 * var(--spacing-03));
  margin: 0 var(--spacing-03);
  height: 100%;
  box-sizing: border-box;
  border-bottom: 1px solid var(--divider-default);
  border-radius: var(--border-radius-default);
  cursor: pointer;
  display: flex;
  background: ${(props) =>
    props.isSelected ? 'var(--blue-20)' : 'transparent'};
`;

const MeetingName = styled.div`
  color: var(--text-primary);
  ${Body01};
`;

const MeetingTime = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
`;

const MeetingTimeAndParticipants = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-03);
  ${HelperText01};
  color: var(--text-tertiary);
`;

const MeetingParticipants = styled.div`
  ${HelperText01};
`;

const MeetingInterPolationDot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: var(--text-secondary);
`;

const MeetingDetailsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-05);
`;

const LoadingState = styled.div`
  height: 65px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 100%;
  color: var(--text-tertiary);
  gap: var(--spacing-03);
`;

const MeetingNameParticipantsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
`;

const MeetingContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-04);
  padding: 0px var(--spacing-05);
`;

const SelectedIcon = styled(FontAwesomeIcon)<{ isSelected: boolean }>`
  opacity: ${(props) => (props.isSelected ? 1 : 0)};
`;

const LoadingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 65px;
  color: var(--text-secondary);
`;

const DEFAULT_LIST_WIDTH = 700;

export const MeetingList = ({
  meetings: externalMeetings,
  isLoading: externalIsLoading,
  isDropdownOpen,
  selectedMeetingId,
  handleMenuClick,
  filter,
  listWidth = DEFAULT_LIST_WIDTH,
}: {
  meetings?: Meeting[];
  isLoading: boolean;
  selectedMeetingId: number | undefined;
  isDropdownOpen?: boolean;
  handleMenuClick: (meetingId: number, meeting: Meeting) => void;
  filter?: GroupedFilter;
  listWidth?: number;
}) => {
  const {
    meetings: paginatedMeetings,
    isLoading: isPaginatedLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAllMeetingsQueryWithScrollPagination(filter, {
    enabled: isDropdownOpen || false,
    limit: 25,
  });

  // Use external meetings if provided, otherwise use paginated meetings
  const meetingList = useMemo(() => {
    return externalMeetings || paginatedMeetings || [];
  }, [externalMeetings, paginatedMeetings]);

  const isLoading = externalIsLoading || isPaginatedLoading;

  // Handle scroll to load more data when near bottom
  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }: { visibleStopIndex: number }) => {
      const threshold = 5; // Load more when 5 items from bottom

      if (
        hasNextPage &&
        !isFetchingNextPage &&
        visibleStopIndex >= meetingList.length - threshold
      ) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage, meetingList.length],
  );

  // Calculate total item count (current items + 1 if more to load)
  const itemCount = hasNextPage ? meetingList.length + 1 : meetingList.length;

  const Row = ({ index, style }) => {
    const meeting = meetingList[index];

    // Show loading item if we're at the end and fetching more
    if (!meeting) {
      return (
        <RowWrapper style={style}>
          <LoadingItem>
            {isFetchingNextPage ? (
              <>
                <Loading size="small" />
                <Body01>Loading more meetings...</Body01>
              </>
            ) : (
              <Body01>No more meetings</Body01>
            )}
          </LoadingItem>
        </RowWrapper>
      );
    }

    const isSelected = selectedMeetingId === meeting.meetingId;
    const hasParticipants = meeting.meta?.participants?.length > 0;

    return (
      <RowWrapper style={style}>
        <MenuItem
          isSelected={isSelected}
          onClick={() => handleMenuClick(meeting.meetingId, meeting)}
        >
          <MeetingContainer>
            <SelectedIcon isSelected={isSelected} icon={faCheck} />
            <MeetingDetailsWrapper>
              <MeetingNameParticipantsWrapper>
                <MeetingName>{meeting.meetingName}</MeetingName>
                <MeetingTimeAndParticipants>
                  <MeetingTime>
                    {moment(meeting.startTime).format('D MMM h:mmA')}
                  </MeetingTime>
                  <MeetingInterPolationDot />
                  <Tooltip
                    title={
                      hasParticipants
                        ? meeting.meta?.participants
                            ?.slice(1)
                            .map((p) => p.firstName || p.emailId)
                            .join(', ')
                        : ''
                    }
                  >
                    <MeetingParticipants>
                      {hasParticipants ? (
                        <>
                          {meeting.meta.participants
                            .slice(0, 1)
                            .map((p) => p.firstName || p.emailId)
                            .join(', ')}
                          {meeting.meta.participants.length > 1 &&
                            ` and ${meeting.meta.participants.length - 1} ${
                              meeting.meta.participants.length - 1 === 1
                                ? 'other'
                                : 'others'
                            }`}
                        </>
                      ) : (
                        'No participants'
                      )}
                    </MeetingParticipants>
                  </Tooltip>
                </MeetingTimeAndParticipants>
              </MeetingNameParticipantsWrapper>
            </MeetingDetailsWrapper>
          </MeetingContainer>
        </MenuItem>
      </RowWrapper>
    );
  };

  return (
    <div>
      {isLoading && meetingList.length === 0 ? (
        <RowWrapper>
          <LoadingState>
            <Loading size="default" />
            <div>Loading meetings...</div>
          </LoadingState>
        </RowWrapper>
      ) : (
        <List
          height={400}
          itemCount={itemCount}
          itemSize={65}
          width={listWidth}
          onItemsRendered={handleItemsRendered}
        >
          {Row}
        </List>
      )}
    </div>
  );
};
